import type { Book, Bottle, DrinkKind } from '../../shared/api';
import { DRINK_KIND_LABELS } from '../../shared/api';
import { isWholeUnit, needsCheck, onShoppingList, unitsLeft } from './bottles';

/** Numbers for the Home page, from the lists the app already loads. */
export interface BookStats {
  books: number;
  pages: number;
  read: number;
  toBeRead: number;
  wishlist: number;
  /** Added in the last 30 days. */
  recent: number;
  /** The author with the most books, when there's a clear one (and at least two books). */
  topAuthor: { name: string; count: number } | null;
  /** The largest category of the household's own (non built-in), if any book is in one. */
  biggestOwnCategory: { name: string; count: number } | null;
}

export interface BarStats {
  inStock: number;
  open: number;
  worthChecking: number;
  shoppingList: number;
  finished: number;
  /** Litres on hand, across every bottle with a known size. */
  litres: number;
  /** The most common kind in stock, when there are at least two of it. */
  topKind: { kind: DrinkKind; label: string; count: number } | null;
}

const BUILT_IN = new Set(['Owned', 'Wishlist', 'To be read', 'Read', 'Loaned']);

function top<T extends string>(counts: Map<T, number>): { key: T; count: number } | null {
  let best: { key: T; count: number } | null = null;
  for (const [key, count] of counts) if (!best || count > best.count) best = { key, count };
  return best;
}

export function bookStats(books: readonly Book[], now = new Date()): BookStats {
  const cutoff = now.getTime() - 30 * 24 * 60 * 60_000;
  const inCategory = (name: string) => books.filter((b) => b.categories.some((c) => c.name === name)).length;
  const authors = new Map<string, number>();
  const own = new Map<string, number>();
  for (const book of books) {
    for (const author of book.authors) authors.set(author, (authors.get(author) ?? 0) + 1);
    for (const category of book.categories) if (!BUILT_IN.has(category.name)) own.set(category.name, (own.get(category.name) ?? 0) + 1);
  }
  const author = top(authors);
  const category = top(own);
  return {
    books: books.length,
    pages: books.reduce((sum, b) => sum + (b.numberOfPages ?? 0), 0),
    read: inCategory('Read'),
    toBeRead: inCategory('To be read'),
    wishlist: inCategory('Wishlist'),
    recent: books.filter((b) => new Date(b.addedAt).getTime() >= cutoff).length,
    topAuthor: author && author.count >= 2 ? { name: author.key, count: author.count } : null,
    biggestOwnCategory: category ? { name: category.key, count: category.count } : null,
  };
}

export function barStats(bottles: readonly Bottle[]): BarStats {
  const stocked = bottles.filter((b) => b.quantity > 0);
  const kinds = new Map<DrinkKind, number>();
  for (const bottle of stocked) kinds.set(bottle.kind, (kinds.get(bottle.kind) ?? 0) + 1);
  const kind = top(kinds);
  return {
    inStock: stocked.length,
    open: stocked.filter((b) => !isWholeUnit(b) && b.level < 0.99).length,
    worthChecking: bottles.filter((b) => needsCheck(b)).length,
    shoppingList: bottles.filter(onShoppingList).length,
    finished: bottles.length - stocked.length,
    litres: Math.round(stocked.reduce((sum, b) => sum + (b.sizeMl ? (unitsLeft(b) * b.sizeMl) / 1000 : 0), 0) * 10) / 10,
    // Only a clear favourite: with one of each, "mostly wine (1)" would be silly.
    topKind: kind && kind.count >= 2 ? { kind: kind.key, label: DRINK_KIND_LABELS[kind.key], count: kind.count } : null,
  };
}

/** 12345 → "12,345". */
export function formatCount(n: number): string {
  return n.toLocaleString();
}
